/**
 * Web-only: register @font-face rules for app fonts bundled under assets/fonts.
 *
 * Must match `font-family` strings from theme/fonts.ts for Platform.OS === 'web'.
 * RNW Platform.select uses `default` when no `web` key exists — keep it that way.
 * Do NOT add `web:` overrides in theme/fonts.ts; @font-face family names are
 * taken from GTFonts/PoppinsFonts/LatoFonts at inject time and must stay
 * GT-Walsheim-* / Poppins-* / Lato-*.
 *
 * Poppins-SemiBold / Poppins-Bold: theme references these but there are no matching
 * files in assets/fonts yet — do not register fake faces; browsers will fall back
 * until those files are added and listed here.
 *
 * GT Walsheim: confirm license allows embedding in your web/Electron distribution.
 */

import { GTFonts, LatoFonts, PoppinsFonts } from '../theme/fonts';
import gtBold from '../assets/fonts/GT-Walsheim/GT-Walsheim-Bold.otf';
import gtRegular from '../assets/fonts/GT-Walsheim/GT-Walsheim-Regular.otf';
import gtSemi from '../assets/fonts/GT-Walsheim/GT-Walsheim-SemiBold.otf';
import gtThin from '../assets/fonts/GT-Walsheim/GT-Walsheim-Thin.otf';
import latoRegular from '../assets/fonts/Lato/Lato-Regular.ttf';
import poppinsRegular from '../assets/fonts/Poppins/Poppins-Regular.ttf';

const STYLE_ID = 'bizplus-app-fonts';
const twemojiMozilla =
  require('../node_modules/rn-country-select/lib/assets/fonts/TwemojiMozilla.woff2') as string;

function fontFace(
  fontFamily: string,
  src: string,
  extra = 'font-display:swap;',
) {
  const family = JSON.stringify(fontFamily);
  const url = JSON.stringify(src);
  return `@font-face{font-family:${family};src:url(${url});${extra}}`;
}

export function injectAppFontsOnce() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;

  const css = [
    fontFace(GTFonts.regular, gtRegular, 'font-weight:400;font-display:swap;'),
    fontFace(GTFonts.semibold, gtSemi, 'font-weight:600;font-display:swap;'),
    fontFace(GTFonts.bold, gtBold, 'font-weight:700;font-display:swap;'),
    fontFace(GTFonts.thin, gtThin, 'font-weight:100;font-display:swap;'),
    fontFace(
      PoppinsFonts.regular,
      poppinsRegular,
      'font-weight:400;font-display:swap;',
    ),
    fontFace(
      PoppinsFonts.base,
      poppinsRegular,
      'font-weight:400;font-display:swap;',
    ),
    fontFace(
      LatoFonts.regular,
      latoRegular,
      'font-weight:400;font-display:swap;',
    ),
    fontFace(LatoFonts.base, latoRegular, 'font-weight:400;font-display:swap;'),
    fontFace('TwemojiMozilla', twemojiMozilla, 'font-display:block;'),
  ].join('\n');

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.type = 'text/css';
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
}
